/* src/pages/Dashboard/Profile/Edit/Profile.jsx
   ------------------------------------------------------------- */
import React, { useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Backdrop, CircularProgress, useTheme } from "@mui/material";
import axios from "axios";

import backend from "../../../../../api";
import { setUserData } from "../../../../../redux-store/auth/actions";

import Classes from "./Profile.module.css";

import BasicInfo from "./Comp/BasicInfo";
import Experience from "./Comp/Experience";
import SessionDetails from "./Comp/SessionDetails";
import BookingSetup from "./Comp/BookingSetup";

import {
  ProfileIcon,
  BriefCaseIcon,
  CreditCardIcon,
} from "../../../../../utils/Icons";
import { enqueueSnackbar } from "notistack";

export default function Profile() {
  const { user } = useSelector((s) => s.auth);
  const dispatch = useDispatch();
  const theme = useTheme();

  const [active, setActive] = useState(1);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  /* ───────── upload helper ───────── */
  const uploadMedia = async (files) => {
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const res = await axios.post(
        "https://mentr-aws-server-668d71936193.herokuapp.com/api/deck/upload",
        fd
      );
      console.log(res.data.data[0]?.imgurl)
      return res.data.data[0]?.imgurl;
    } finally {
      setUploading(false);
    }
  };

  /* ───────── save helper ───────── */
  const saveProfile = useCallback(
    async (patch) => {
      setSaving(true);
      try {
        const payload = {
          mentorProfile: { ...user.mentorProfile, ...patch },
        };
        const { data } = await backend.put(`/api/user/${user._id}`, payload);
        dispatch(setUserData({ ...user, ...data.data }));
        enqueueSnackbar("Saved Successfully!", { variant: "success" });
      } finally {
        setSaving(false);
      }
    },
    [dispatch, user]
  );

  /* ───────── step renderer ───────── */
  const Step = (() => {
    switch (active) {
      case 1:
        return (
          <BasicInfo
            initial={user?.mentorProfile?.basicInfo}
            uploadMedia={uploadMedia}
            saveProfile={saveProfile}
          />
        );
      case 2:
        return (
          <Experience
            initial={user?.mentorProfile?.experience}
            uploadMedia={uploadMedia}
            saveProfile={saveProfile}
          />
        );
      case 3:
        return (
          <SessionDetails
            initial={user?.mentorProfile?.sessionDetails}
            saveProfile={saveProfile}
          />
        );
      case 4:
        return (
          <BookingSetup
            initial={user?.mentorProfile?.bookingSetup}
            saveProfile={saveProfile}
          />
        );
      default:
        return null;
    }
  })();

  const steps = [
    { id: 1, label: "Basic Info", icon: <ProfileIcon height="17" width="17" fill="white" /> },
    { id: 2, label: "Experience", icon: <BriefCaseIcon height="17" width="17" fill="white" /> },
    { id: 3, label: "Session Details", icon: <img src="/assets/icons/session-details.png" height={17} width={17} /> },
    { id: 4, label: "Payments", icon: <CreditCardIcon height="17" width="17" fill="white" /> },
  ];

  return (
    <>
      <Backdrop open={saving || uploading} sx={{ zIndex: theme.zIndex.tooltip + 1, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <div className={Classes.container}>
        <div className={Classes.header}>
          <div>
            <h3 className={Classes.title3}>Profile</h3>
            <h5 className={Classes.text}>Manage your mentor profile.</h5>
          </div>
        </div>

        <div className={Classes.hero}>
          {/* sidebar */}
          <div className={Classes.sidebar}>
            {steps.map((s) => (
              <div
                key={s.id}
                role="button"
                onClick={() => setActive(s.id)}
                className={`${Classes.sideElement} ${active === s.id ? Classes.current : ""}`}
              >
                <div className={Classes.flexAlign10}>
                  <div className={`${Classes.imageCon} ${active === s.id ? Classes.active : ""}`}>{s.icon}</div>
                  <h5 className={active === s.id ? Classes.activeText : ""}>{s.label}</h5>
                </div>
              </div>
            ))}
          </div>

          {/* main */}
          {Step}
        </div>
      </div>
    </>
  );
}
